/* eslint-disable react/display-name */
import React, { ComponentType } from 'react';
import styles from './styles.module.scss';
import { useIntersectionObserver } from '@starsoft/common/hooks';

/**
 * Higher-Order Component (HOC) for lazy loading a component based on its visibility in the viewport.
 *
 * @template T - Type of the props that the wrapped component accepts.
 * @param {ComponentType<T>} WrappedComponent - The component to be lazy-loaded.
 * @param {number} [threshold=0] - The threshold for the intersection observer. Default is 0.
 * @returns {React.FC<T>} A functional component that wraps the given component and lazy loads it.
 *
 * @example
 * // Usage example:
 * const LazyLoadedComponent = withLazyLoad(MyComponent, 0.5);
 *
 * function App() {
 *   return (
 *     <div>
 *       <LazyLoadedComponent prop1="value1" prop2="value2" />
 *     </div>
 *   );
 * }
 */
export const withLazyLoad = <T extends object>(
  WrappedComponent: ComponentType<T>,
  threshold = 0,
): React.FC<T> => {
  return (props: T) => {
    const [isInView, ref, bufferRef] = useIntersectionObserver(threshold);

    return (
      <div className={styles.container}>
        <div className={styles.container__buffer} ref={bufferRef} />
        <div ref={ref} className={styles.container__content}>
          {isInView ? <WrappedComponent {...props} /> : null}
        </div>
      </div>
    );
  };
};
